import DataProcessor from './data-processor'
import orderBy from 'lodash/orderBy'
import get from 'lodash/get'
import dayjs from 'dayjs'

function lowercasedIteratee(key) {
  return row => {
    const value = get(row, key)

    if (typeof value === 'string') {
      return value.toLowerCase()
    }

    return value
  }
}

class StaticProcessor extends DataProcessor {
  constructor(params) {
    const { data = [] } = params

    super(params)

    this.fullData = [].concat(data)

    this.init()
  }

  sendRequest() {
    return new Promise(resolve => {
      setTimeout(
        resolve,
        Math.floor(Math.random() * 950) + 300, {
          data: this.fullData,
          total: this.total,
        }
      )
    })
      .then(res => this.filter(res))
      .then(data => this.sort(data))
      .then(data => this.paginate(data))
  }

  sendRequestAll() {
    return new Promise(resolve => {
      setTimeout(
        resolve,
        Math.floor(Math.random() * 2500) + 1500, {
          data: this.fullData,
          total: this.total,
        }
      )
    })
      .then(res => this.filter(res))
      .then(data => this.sort(data))
  }

  filterBoolean(acc, filter) {
    const {
      attribute,
      comparison,
      booleanValues,
    } = filter

    return acc.filter(row => {
      return row[attribute] === booleanValues[comparison].value
    })
  }

  filterDate(acc, filter) {
    const {
      value,
      attribute,
      comparison,
    } = filter
    const filterDate = dayjs(value).startOf('day')

    return acc.filter(row => {
      const rowDate = dayjs(row[attribute]).startOf('day')
      switch (comparison) {
        case 'before':
          return rowDate.isBefore(filterDate)
        case 'after':
          return filterDate.add(1, 'd').isBefore(rowDate)
        case 'on':
          return filterDate.isSame(rowDate)
        default:
          console.warn(`unknown date filter comparison: ${comparison}`)
          return false
      }
    })
  }

  filterNumeric(acc, filter) {
    const {
      comparison,
      attribute,
      value,
    } = filter

    switch (comparison) {
      case 'gt':
        return acc.filter(row => value < row[attribute])
      case 'gte':
        return acc.filter(row => value <= row[attribute])
      case 'lt':
        return acc.filter(row => value > row[attribute])
      case 'lte':
        return acc.filter(row => value >= row[attribute])
      case 'eq':
        return acc.filter(row => +value === +row[attribute])
      default:
        console.warn(`unknown numeric filter comparison: ${comparison}`)
        return false
    }
  }

  filterSelect(acc, filter) {
    const {
      attribute,
      value,
    } = filter

    return acc.filter(row => {
      const rowValue = row[attribute]

      if (Array.isArray(rowValue)) {
        for (const item of rowValue) {
          if (value.includes(item)) {
            return true
          }
        }
        return false
      } else {
        return value.includes(rowValue)
      }
    })
  }

  filterString(acc, filter) {
    const {
      comparison,
      attribute,
      value,
    } = filter

    return acc.filter(row => {
      const rowValue = row[attribute]
      let testRE
      let cellValue

      if (Array.isArray(rowValue)) {
        for (const item of rowValue) {
          testRE = new RegExp(value.toLowerCase(), 'g')
          cellValue = item.toLowerCase()
          if (testRE.test(cellValue)) {
            return true
          }
        }
        return false
      } else {
        cellValue = row[attribute].toLowerCase()
        switch (comparison) {
          case 'contains':
            return cellValue.includes(value.toLowerCase())
          case 'doesnt_contain':
            return !cellValue.includes(value.toLowerCase())
          case 'eq':
            return cellValue === value.toLowerCase()
          case 'starts':
            return cellValue.startsWith(value.toLowerCase())
          case 'ends':
            return cellValue.endsWith(value.toLowerCase())
        }
      }
    })
  }

  filter(response) {
    const {
      filters,
    } = this.dataQuery
    const newData = filters.reduce((acc, filter) => {
      const {
        type,
      } = filter

      switch (type) {
        case 'date':
          return this.filterDate(acc, filter)
        case 'numeric':
          return this.filterNumeric(acc, filter)
        case 'boolean':
          return this.filterBoolean(acc, filter)
        case 'select':
          return this.filterSelect(acc, filter)
        case 'string':
          return this.filterString(acc, filter)
        default:
          console.warn(`unknown filter type: ${type}`)
          return []
      }
    }, response.data)

    return {
      data: newData,
      total: newData.length,
    }
  }

  sort(response) {
    const {
      sort,
      defaultSort,
    } = this.dataQuery

    const byDefault = Object.keys(sort).length === 0
    const sortKeys = Object.keys(byDefault ? defaultSort || {} : sort)
    const sortOrders = sortKeys.length ? sortKeys.reduce((acc, key) => {
      acc.push(byDefault ? defaultSort[key] : sort[key])
      return acc
    }, []) : []

    return {
      data: orderBy(response.data, sortKeys.map(lowercasedIteratee), sortOrders),
      total: response.total,
    }
  }

  paginate(response) {
    const {
      page,
      pageSize,
    } = this.dataQuery
    const from = (page - 1) * pageSize
    const to = page * pageSize

    return {
      data: response.data.slice(from, to),
      total: response.total,
    }
  }

  manualUpdate(newData) {
    return new Promise(resolve => {
      this.fullData = newData
      this.total = newData.length
      Promise.resolve()
        .then(() => this.filter({
          data: this.fullData,
          total: this.total,
        }))
        .then(data => this.sort(data))
        .then(data => this.paginate(data))
        .then(({ data, total }) => {
          this.data = data
          this.total = total
          resolve(this.data)
        })
    })
  }
}

export default StaticProcessor
